// First-party attribution payload carried on the pk_attribution cookie. Stored
// server-side on users.attribution (jsonb) once the signed-up visitor arrives
// back from WorkOS. See PK-97 for the end-to-end design.
//
// Sanitisation is applied at every ingest point (marketing writer, app reader,
// server stamper) because each layer sees adversarial input: a crafted URL,
// a tampered cookie, a replayed request body.

export interface AttributionPayload {
  params: Record<string, string>;
  referrer: string;
  landing_path: string;
  landed_at: string;
}

export const ATTR_KEY_MAX = 64;
export const ATTR_VALUE_MAX = 512;
export const ATTR_TOTAL_MAX = 2048;
export const ATTR_COOKIE_NAME = "pk_attribution";
export const ATTR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

// Lower-cased exact-match denylist of param keys that are likely to leak PII
// or secrets. A mistaken campaign URL like "?email=foo@bar.com" must not end
// up in our cookie, let alone the DB. Kept small and unambiguous — broader
// patterns (regex on "_token") would false-positive on real utm content.
const DENY_KEYS: ReadonlySet<string> = new Set([
  "email",
  "phone",
  "token",
  "access_token",
  "code",
  "password",
  "secret",
  "auth",
]);

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) : s;
}

export function sanitizeParams(
  raw: Readonly<Record<string, unknown>>,
): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const [rawKey, rawVal] of Object.entries(raw)) {
    if (typeof rawKey !== "string" || typeof rawVal !== "string") continue;
    if (rawVal.length === 0) continue;
    if (DENY_KEYS.has(rawKey.toLowerCase())) continue;
    const key = truncate(rawKey, ATTR_KEY_MAX);
    const val = truncate(rawVal, ATTR_VALUE_MAX);
    initial[key] = val;
  }
  return fitToBudget(initial);
}

// Drop biggest-value-first until the JSON encoding fits inside the budget.
// Longest-first sheds mass fastest, which matters when the attack is a single
// stuffed value — the adversarial key is gone on pass one. Kept keys remain
// in their original ordering for predictable downstream serialisation.
function fitToBudget(params: Record<string, string>): Record<string, string> {
  if (JSON.stringify(params).length <= ATTR_TOTAL_MAX) return params;
  const sorted = Object.entries(params).sort(([ak, av], [bk, bv]) => {
    const dv = bv.length - av.length;
    return dv !== 0 ? dv : ak.localeCompare(bk);
  });
  const drop = new Set<string>();
  for (const [k] of sorted) {
    drop.add(k);
    const remaining: Record<string, string> = {};
    for (const [kk, vv] of Object.entries(params)) {
      if (!drop.has(kk)) remaining[kk] = vv;
    }
    if (JSON.stringify(remaining).length <= ATTR_TOTAL_MAX) return remaining;
  }
  return {};
}

export function sanitizeAttribution(raw: unknown): AttributionPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const params =
    r.params && typeof r.params === "object" && !Array.isArray(r.params)
      ? sanitizeParams(r.params as Record<string, unknown>)
      : {};
  const referrer =
    typeof r.referrer === "string" ? truncate(r.referrer, ATTR_VALUE_MAX) : "";
  const landingPath =
    typeof r.landing_path === "string"
      ? truncate(r.landing_path, ATTR_VALUE_MAX)
      : "";
  const landedAt =
    typeof r.landed_at === "string"
      ? truncate(r.landed_at, ATTR_VALUE_MAX)
      : "";
  if (
    Object.keys(params).length === 0 &&
    referrer.length === 0 &&
    landingPath.length === 0
  ) {
    return null;
  }
  return {
    params,
    referrer,
    landing_path: landingPath,
    landed_at: landedAt,
  };
}

export function parseAttributionCookie(
  cookieValue: string,
): AttributionPayload | null {
  if (!cookieValue) return null;
  try {
    const decoded = decodeURIComponent(cookieValue);
    const parsed = JSON.parse(decoded);
    return sanitizeAttribution(parsed);
  } catch {
    return null;
  }
}

export function encodeAttributionCookie(payload: AttributionPayload): string {
  return encodeURIComponent(JSON.stringify(payload));
}

// Read a specific cookie by name from a document.cookie string. Returns the
// raw (still-URI-encoded) value, or null if the cookie is absent. Kept tiny
// and pure so the Astro inline scripts can inline the same shape.
export function readCookie(
  cookieHeader: string,
  name: string,
): string | null {
  if (!cookieHeader) return null;
  const needle = name + "=";
  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trimStart();
    if (trimmed.startsWith(needle)) {
      return trimmed.slice(needle.length);
    }
  }
  return null;
}
